import axios from 'axios';

import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import { createLogger } from '@/utils/logging';

const logger = createLogger('ProjectWorkflowAPI', 'api');

/** Typed error thrown by all project workflow API functions. */
export class ProjectWorkflowApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectWorkflowApiError';
  }
}

/** Request body for creating a new client. */
export interface CreateClientRequestDTO {
  client_name: string;
  client_slug?: string | null;
  industry: string;
  status?: 'active' | 'inactive';
}

/** A single client row as returned by project-workflow endpoints (differs slightly from the directory DTO). */
export interface ClientDirectoryRowDTO {
  client_id: string;
  client_name: string;
  client_slug: string;
  industry: string;
  status: 'active' | 'inactive';
  project_count: number;
  updated_at: string;
}

/** Response returned when a guided project creation prompt session is started. */
export interface StartPromptSessionResponseDTO {
  session_id: string;
  client_id: string;
  status: string;
  current_step: string;
}

/** Request body for submitting a user's answer to the next step of a prompt session. */
export interface AppendPromptResponseRequestDTO {
  step: string;
  payload: Record<string, unknown>;
}

/** Response after appending a prompt answer; includes the next step key. */
export interface AppendPromptResponseDTO {
  session_id: string;
  next_step: string;
  status: string;
}

/** Full project record returned by create and get endpoints. */
export interface CreateProjectFromSessionDTO {
  client_id: string;
  project_id: string;
  project_name: string;
  project_slug: string;
  status: string;
  business_units_in_scope: string[];
  engagement_duration?: string | null;
  assessment_lead?: string | null;
  sow_document_url?: string | null;
  sharepoint_site_id?: string | null;
  sharepoint_drive_id?: string | null;
  sharepoint_folder_item_id?: string | null;
  sharepoint_folder_web_url?: string | null;
  home_route: string;
  created_at: string;
  updated_at: string;
}

/** Partial update payload for a project record; all fields optional. */
export interface UpdateProjectPayload {
  project_name?: string;
  project_slug?: string;
  status?: string;
  business_units_in_scope?: string[];
  engagement_duration?: string | null;
  assessment_lead?: string | null;
  sow_document_url?: string | null;
  sharepoint_site_id?: string | null;
  sharepoint_drive_id?: string | null;
  sharepoint_folder_item_id?: string | null;
  sharepoint_folder_web_url?: string | null;
  /** Sent for unlinked Snowflake projects so the backend can provision a Postgres record on first save. */
  snowflake_project_id?: string | null;
}

/** SharePoint folder coordinates resolved from a sharing link. */
export interface ResolveSharePointSharingLinkResponseDTO {
  sharepoint_site_id: string;
  sharepoint_drive_id: string;
  sharepoint_folder_item_id: string;
  sharepoint_folder_web_url: string;
}

/** Paginated list of project records. */
export interface ProjectWorkflowListResponseDTO {
  items: CreateProjectFromSessionDTO[];
  total: number;
}

const projectApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Normalises any caught value into a ProjectWorkflowApiError, extracting the backend `detail`
 * message where available.
 */
const parseProjectApiError = (error: unknown): ProjectWorkflowApiError => {
  handleSessionExpired(error);

  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Request failed';
    return new ProjectWorkflowApiError(message);
  }

  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return new ProjectWorkflowApiError(detail);
  }
  if (detail && typeof detail === 'object') {
    const nested = (detail as Record<string, unknown>).detail;
    if (typeof nested === 'string' && nested.trim().length > 0) {
      return new ProjectWorkflowApiError(nested);
    }
  }

  return new ProjectWorkflowApiError(error.message);
};

/**
 * Creates a new client record.
 * POST /clients
 *
 * @returns The newly created client row.
 */
export const createClient = async (
  accessToken: string,
  payload: CreateClientRequestDTO,
): Promise<ClientDirectoryRowDTO> => {
  try {
    const response = await projectApiClient.post<ClientDirectoryRowDTO>('/clients', payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to create client', parsed);
    throw parsed;
  }
};

/**
 * Starts a guided multi-step prompt session for creating a new project under a client.
 * POST /clients/{clientId}/projects/prompt-session/start
 *
 * @param initiatedBy - Optional user identifier recorded for audit purposes.
 * @returns Session handle with the first step key.
 */
export const startProjectPromptSession = async (
  accessToken: string,
  clientId: string,
  initiatedBy?: string | null,
): Promise<StartPromptSessionResponseDTO> => {
  try {
    const response = await projectApiClient.post<StartPromptSessionResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects/prompt-session/start`,
      {
        initiated_by: initiatedBy ?? null,
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to start prompt session', parsed);
    throw parsed;
  }
};

/**
 * Submits the user's answer for the current step of an in-progress prompt session.
 * POST /clients/{clientId}/projects/prompt-session/{sessionId}/responses
 *
 * @returns The next step key to display.
 */
export const appendProjectPromptResponse = async (
  accessToken: string,
  clientId: string,
  sessionId: string,
  payload: AppendPromptResponseRequestDTO,
): Promise<AppendPromptResponseDTO> => {
  try {
    const response = await projectApiClient.post<AppendPromptResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects/prompt-session/${encodeURIComponent(sessionId)}/responses`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to append prompt response', parsed);
    throw parsed;
  }
};

/**
 * Finalises a prompt session and creates the project record.
 * POST /clients/{clientId}/projects
 *
 * @returns The newly created project.
 */
export const createProjectFromPromptSession = async (
  accessToken: string,
  clientId: string,
  payload: { session_id: string; create_defaults?: boolean },
): Promise<CreateProjectFromSessionDTO> => {
  try {
    const response = await projectApiClient.post<CreateProjectFromSessionDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to create project from prompt session', parsed);
    throw parsed;
  }
};

/**
 * Lists all projects for a client.
 * GET /clients/{clientId}/projects
 *
 * @returns Paginated list of project records.
 */
export const listClientProjects = async (
  accessToken: string,
  clientId: string,
): Promise<ProjectWorkflowListResponseDTO> => {
  try {
    const response = await projectApiClient.get<ProjectWorkflowListResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to list projects for client', parsed);
    throw parsed;
  }
};

/**
 * Fetches a single project by ID.
 * GET /clients/{clientId}/projects/{projectId}
 */
export const getClientProject = async (
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<CreateProjectFromSessionDTO> => {
  try {
    const response = await projectApiClient.get<CreateProjectFromSessionDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to get project for client', parsed);
    throw parsed;
  }
};

/**
 * Applies a partial update to an existing project.
 * PATCH /clients/{clientId}/projects/{projectId}
 *
 * @returns The updated project record.
 */
export const updateProject = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  payload: UpdateProjectPayload,
): Promise<CreateProjectFromSessionDTO> => {
  try {
    const response = await projectApiClient.patch<CreateProjectFromSessionDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to update project', parsed);
    throw parsed;
  }
};

/**
 * Sets the SharePoint folder URL on a project. Returns void; caller reacts to
 * the resolved/rejected promise for modal loading and error state.
 * PATCH /clients/{clientId}/projects/{projectId}
 */
export const patchProjectSharePointUrl = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  url: string,
): Promise<void> => {
  try {
    await projectApiClient.patch(
      `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}`,
      { sharepoint_folder_web_url: url },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to set SharePoint URL', parsed);
    throw parsed;
  }
};

/**
 * Resolves a SharePoint sharing link to the underlying site/drive/folder IDs for project storage.
 * POST /clients/{clientId}/projects/{projectId}/sharepoint/resolve-sharing-link
 *
 * @param sharingUrl - The full SharePoint sharing URL (copy link).
 * @param microsoftOboAssertion - Optional Entra OBO token for Graph API access.
 * @returns The resolved SharePoint folder coordinates.
 */
export const resolveSharePointSharingLink = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  sharingUrl: string,
  microsoftOboAssertion?: string | null,
): Promise<ResolveSharePointSharingLinkResponseDTO> => {
  try {
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
    const obo = microsoftOboAssertion?.trim();
    if (obo) {
      headers['X-Microsoft-OBO-Assertion'] = obo;
    }
    const response = await projectApiClient.post<ResolveSharePointSharingLinkResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/sharepoint/resolve-sharing-link`,
      { sharing_url: sharingUrl },
      {
        headers,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseProjectApiError(error);
    logger.error('Failed to resolve SharePoint sharing link', parsed);
    throw parsed;
  }
};
